import { ComponentPropsWithoutRef, ReactNode } from "react";
import ReactModal from "react-modal";
import CloseIcon from "../_icons/closeIcon";
import clsx from "clsx";
import { useTranslations } from "next-intl";

type ModalProps = ComponentPropsWithoutRef<typeof ReactModal> & {
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  hideCloseButton?: boolean;
  closeIcon?: ReactNode;
  closeButtonClassName?: string;
  focusElementId?: string;
  closeModalLabel?: string;
};

export const RegularModal = ({
  isOpen,
  children,
  className = "",
  overlayClassName = "",
  contentClassName = "",
  ...rest
}: ModalProps) => {

  return (
    <BaseModal
      isOpen={isOpen}
      className={`fixed inset-0 flex items-center justify-center ${className}`}
      overlayClassName={`fixed inset-0 bg-black ${overlayClassName} z-50`}
      contentClassName={contentClassName}
      {...rest}
    >
      {children}
    </BaseModal>
  );
};

export const BottomModal = ({
  isOpen,
  children,
  className = "",
  overlayClassName = "",
  ...rest
}: ModalProps) => {

  return (
    <BaseModal
      isOpen={isOpen}
      className={`fixed inset-0 flex items-end sm:items-center justify-center ${className}`}
      overlayClassName={`fixed inset-0 bg-black ${overlayClassName} z-50`}
      {...rest}
    >
      {children}
    </BaseModal>
  );
};

export const DrawerModal = ({
  isOpen = false,
  children,
  className = "",
  overlayClassName = "",
  style = {
    overlay: {
      zIndex: 1000,
    },
  },
  onRequestClose,
  ...rest
}: ModalProps) => {
  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={true}
      className={`fixed top-0 right-0 bottom-0 sm:w-[400px] bg-white text-black shadow-md focus:outline-none flex flex-col w-full ${className}`}
      overlayClassName={`fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 ${overlayClassName}`}
      style={style}
      {...rest}
    >
      {children}
    </BaseModal>
  );
};

const BaseModal = ({
  isOpen = false,
  onRequestClose,
  children,
  ariaHideApp = false,
  appElement = typeof window !== "undefined"
    ? document.getElementById("app") || undefined
    : undefined,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
  className,
  overlayClassName,
  style,
  contentClassName,
  hideCloseButton,
  closeIcon,
  closeButtonClassName = "top-4 right-4",
  focusElementId,
  onAfterOpen,
  closeModalLabel,
  ...rest
}: ModalProps) => {
  const t = useTranslations("common");

  const handleAfterOpen = () => {
    if (onAfterOpen) onAfterOpen();

    if (focusElementId) {
      setTimeout(() => {
        const el = document.getElementById(focusElementId);
        if (el) el.focus();
      }, 50);
    }
  };

  return (
    <ReactModal
      ariaHideApp={ariaHideApp}
      appElement={appElement}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      className={className}
      overlayClassName={overlayClassName}
      style={style}
      onAfterOpen={handleAfterOpen}
      {...rest}
    >
      <div className={clsx("relative", contentClassName)}>
        {children}
        {onRequestClose && !hideCloseButton && (
          <button
            onClick={onRequestClose}
            className={clsx(
              "absolute top-4 right-4 p-2 rounded-full z-50",
              closeButtonClassName
            )}
            aria-label={closeModalLabel ?? t("closeModalButtonLabel")}
          >
            {closeIcon || <CloseIcon />}
          </button>
        )}
      </div>
    </ReactModal>
  );
};
